/**
 * The general ledger (`/c/$companyId/reports/ledger?scope&account&from&to`):
 * lines grouped by account, each group with its opening, its lines and their
 * running balance, and its closing; paged with Load more. Every line opens its
 * entry.
 */
import type { JSX } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Button,
  CodeEntityCombobox,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  DateField,
  EmptyState,
  PageHeader,
  Skeleton,
  TableSkeleton,
  nonEmpty,
} from "@linkhr/design-system";
import { useTranslation } from "@linkhr/i18n";
import { dateToIso, isoToDate, todayIso } from "@linkhr/i18n/format";
import { useAccounts, useCompanyBooks, useGeneralLedger } from "../api/ledger";
import { useBackKey } from "../components/keys";
import { CODES } from "../ledger/books";
import { useLedgerAccess } from "../ledger/useLedgerAccess";
import { useLedgerFormat } from "../ledger/format";
import { NoReports, ReportError } from "./notices";
import { defaultRange, endOfMonth, firstOfMonth } from "./range";
import { mergeLedgerPages, zero } from "./rows";

export const LEDGER_SCOPES = ["ACCOUNT", "GROUP", "ALL", "PROFIT_AND_LOSS"] as const;
export type LedgerScope = (typeof LEDGER_SCOPES)[number];

export interface LedgerSearch {
  scope: LedgerScope;
  account?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export function LedgerScreen({
  companyId,
  search,
  onSearch,
}: {
  companyId: string;
  search: LedgerSearch;
  onSearch: (next: LedgerSearch) => void;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  const access = useLedgerAccess(companyId);
  const allowed = access.can(CODES.report);
  const books = useCompanyBooks(companyId);
  const accounts = useAccounts(companyId);
  useBackKey();
  const today = books.data !== undefined ? todayIso(books.data.timeZone) : "";
  const range = defaultRange(today, access.window);
  const from = search.from ?? range.from;
  const to = search.to ?? range.to;
  // ACCOUNT and GROUP name their account; without one the scope is every account.
  const scope =
    search.account === undefined && (search.scope === "ACCOUNT" || search.scope === "GROUP")
      ? "ALL"
      : search.scope;
  const gl = useGeneralLedger(
    companyId,
    {
      scope,
      ...(search.account !== undefined && (scope === "ACCOUNT" || scope === "GROUP")
        ? { accountId: search.account }
        : {}),
      from,
      to,
    },
    allowed && books.data !== undefined && from <= to,
  );
  if (!allowed) return <NoReports />;
  if (books.data === undefined) {
    return books.isError ? (
      <ReportError
        error={books.error}
        onRetry={() => void books.refetch()}
        onIntoWindow={() => undefined}
      />
    ) : (
      <Skeleton height={320} aria-label={nonEmpty(t("accounting:loading"))} />
    );
  }
  const set = (changes: Partial<LedgerSearch>): void =>
    onSearch({ scope, account: search.account, from, to, ...changes });
  const pages = gl.data?.pages ?? [];
  const head = pages[0];
  const groups = mergeLedgerPages(pages);
  const currency = head?.currency ?? books.data.functionalCurrency;
  const title =
    head?.account !== null && head?.account !== undefined
      ? `${head.account.code} ${f.name(head.account.name)}`
      : scope === "PROFIT_AND_LOSS"
        ? t("accounting:ledger.reports.gl.income")
        : t("accounting:ledger.reports.gl.all");
  const chart = (accounts.data ?? []).map((account) => ({
    id: account.id,
    code: account.code,
    nameEn: account.name.en ?? account.name.ar ?? "",
    nameAr: account.name.ar ?? account.name.en ?? "",
  }));
  const dateField = (
    label: string,
    value: string,
    onChange: (iso: string) => void,
  ): JSX.Element => (
    <span className="flex flex-col gap-2xs text-label text-muted">
      {label}
      <DateField
        aria-label={label}
        {...(isoToDate(value) !== undefined ? { value: isoToDate(value) as Date } : {})}
        onChange={(date) => {
          if (date !== undefined) onChange(dateToIso(date));
        }}
      />
    </span>
  );

  return (
    <div className="flex flex-col gap-md">
      <PageHeader title={t("accounting:ledger.reports.gl.title", { name: title })} />
      <div className="flex flex-wrap items-end gap-md">
        <span className="flex min-w-64 flex-col gap-2xs text-label text-muted">
          {t("accounting:ledger.reports.gl.account")}
          <CodeEntityCombobox
            ariaLabel={t("accounting:ledger.reports.gl.account")}
            placeholder={t("accounting:ledger.reports.gl.all")}
            value={scope === "ACCOUNT" || scope === "GROUP" ? (search.account ?? "") : ""}
            items={chart}
            onChange={(id) => {
              const picked = accounts.data?.find((account) => account.id === id);
              set(
                picked === undefined
                  ? { scope: "ALL", account: undefined }
                  : { scope: picked.isGroup ? "GROUP" : "ACCOUNT", account: picked.id },
              );
            }}
          />
        </span>
        {dateField(t("accounting:ledger.reports.from"), from, (next) => set({ from: next }))}
        {dateField(t("accounting:ledger.reports.to"), to, (next) => set({ to: next }))}
      </div>

      {from > to ? (
        <Alert variant="warning" description={t("accounting:ledger.reports.range_invalid")} />
      ) : gl.isError && pages.length === 0 ? (
        <ReportError
          error={gl.error}
          onRetry={() => void gl.refetch()}
          onIntoWindow={(window) =>
            set({ from: firstOfMonth(window.to), to: endOfMonth(window.to) })
          }
          onUpTo={(date) => set({ to: date })}
        />
      ) : gl.data === undefined ? (
        <DataTable>
          <TableSkeleton columns={7} aria-label={nonEmpty(t("accounting:loading"))} />
        </DataTable>
      ) : groups.length === 0 ? (
        <EmptyState
          entities={nonEmpty(t("accounting:ledger.reports.gl.none"))}
          nextAction={nonEmpty(t("accounting:ledger.reports.gl.none"))}
          heading={t("accounting:ledger.reports.gl.none")}
          body={t("accounting:ledger.reports.gl.none_body")}
          variant="filtered"
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-subtle">
          <DataTable density="compact">
            <DataTableHeader>
              <tr>
                <DataTableHead>{t("accounting:ledger.reports.gl.columns.date")}</DataTableHead>
                <DataTableHead>{t("accounting:ledger.reports.gl.columns.entry")}</DataTableHead>
                <DataTableHead>
                  {t("accounting:ledger.reports.gl.columns.description")}
                </DataTableHead>
                <DataTableHead>{t("accounting:ledger.reports.gl.columns.contact")}</DataTableHead>
                <DataTableHead className="text-end">
                  {t("accounting:ledger.reports.gl.columns.debit")}
                </DataTableHead>
                <DataTableHead className="text-end">
                  {t("accounting:ledger.reports.gl.columns.credit")}
                </DataTableHead>
                <DataTableHead className="text-end">
                  {t("accounting:ledger.reports.gl.columns.balance")}
                </DataTableHead>
              </tr>
            </DataTableHeader>
            <DataTableBody>
              {groups.flatMap((group, index) => {
                // The last group may go on in the next page: its closing waits for its last line.
                const complete = index < groups.length - 1 || !gl.hasNextPage;
                const money = (amount: string): string => f.money(amount, currency);
                return [
                  <DataTableRow key={`${group.account.id}-head`} className="bg-surface-sunken">
                    <DataTableCell colSpan={7} className="font-semibold">
                      <bdi className="font-mono">{group.account.code}</bdi>{" "}
                      {f.name(group.account.name)}
                    </DataTableCell>
                  </DataTableRow>,
                  <DataTableRow key={`${group.account.id}-opening`}>
                    <DataTableCell colSpan={6} className="text-muted">
                      {t("accounting:ledger.reports.gl.opening", { date: f.day(from) })}
                    </DataTableCell>
                    <DataTableCell className="text-end tabular-nums">
                      {money(group.opening)}
                    </DataTableCell>
                  </DataTableRow>,
                  ...group.lines.map((line) => (
                    <DataTableRow key={line.lineId}>
                      <DataTableCell className="whitespace-nowrap">
                        {f.day(line.accountingDate)}
                      </DataTableCell>
                      <DataTableCell>
                        <Link
                          to="/c/$companyId/entries/$entryId"
                          params={{ companyId, entryId: line.entryId }}
                          className="text-primary underline focus-visible:focus-ring"
                        >
                          <bdi className="font-mono">{line.entryNumber}</bdi>
                        </Link>
                      </DataTableCell>
                      <DataTableCell>{line.description ?? ""}</DataTableCell>
                      <DataTableCell>
                        <bdi>{f.name(line.contact?.name)}</bdi>
                      </DataTableCell>
                      <DataTableCell className="text-end tabular-nums">
                        {zero(line.debit) ? "" : money(line.debit)}
                      </DataTableCell>
                      <DataTableCell className="text-end tabular-nums">
                        {zero(line.credit) ? "" : money(line.credit)}
                      </DataTableCell>
                      <DataTableCell className="text-end tabular-nums">
                        {money(line.runningBalance)}
                      </DataTableCell>
                    </DataTableRow>
                  )),
                  ...(complete
                    ? [
                        <DataTableRow key={`${group.account.id}-closing`} className="font-semibold">
                          <DataTableCell colSpan={4}>
                            {t("accounting:ledger.reports.gl.closing", { date: f.day(to) })}
                          </DataTableCell>
                          <DataTableCell className="text-end tabular-nums">
                            {money(group.debit)}
                          </DataTableCell>
                          <DataTableCell className="text-end tabular-nums">
                            {money(group.credit)}
                          </DataTableCell>
                          <DataTableCell className="text-end tabular-nums">
                            {money(group.closing)}
                          </DataTableCell>
                        </DataTableRow>,
                      ]
                    : []),
                ];
              })}
            </DataTableBody>
            {head !== undefined && !gl.hasNextPage && groups.length > 1 ? (
              <tfoot>
                <DataTableRow className="bg-surface-sunken font-semibold">
                  <DataTableCell colSpan={4}>
                    {t("accounting:ledger.reports.gl.total")}
                  </DataTableCell>
                  <DataTableCell className="text-end tabular-nums">
                    {f.money(head.totals.debit, currency)}
                  </DataTableCell>
                  <DataTableCell className="text-end tabular-nums">
                    {f.money(head.totals.credit, currency)}
                  </DataTableCell>
                  <DataTableCell className="text-end tabular-nums">
                    {f.money(head.closing, currency)}
                  </DataTableCell>
                </DataTableRow>
              </tfoot>
            ) : null}
          </DataTable>
        </div>
      )}
      {gl.hasNextPage ? (
        <span>
          <Button
            variant="secondary"
            disabled={gl.isFetchingNextPage}
            onClick={() => void gl.fetchNextPage()}
          >
            {t("accounting:ledger.reports.load_more")}
          </Button>
        </span>
      ) : null}
      {gl.isError && pages.length > 0 ? (
        <Alert variant="danger" description={t("accounting:errors.load_title")} />
      ) : null}
    </div>
  );
}
